import { useState } from "react";
import { FaImage, FaVideo } from "react-icons/fa";
import {
  PORTAL_REVIEW_STATUS_LABEL,
  PortalReview,
  PortalReviewFile,
  PortalReviewStatus,
} from "../../types/PortalReview";
import { formatAdminDateTime } from "../admin/AdminPageShell";
import { useReviewFileUrl } from "../../hooks/useReviewFileUrl";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { updatePortalReviewStatus } from "../../store/allocation/portalReviewSlice";
import { useToast } from "../utilities/Toast";

const STATUS_CLASS: Record<PortalReviewStatus, string> = {
  pending: "bg-amber-100 text-amber-800",
  useful: "bg-emerald-100 text-emerald-800",
  not_useful: "bg-gray-100 text-gray-600",
  implemented: "bg-sky-100 text-sky-800",
};

function ReviewMedia({
  review,
  file,
}: {
  review: PortalReview;
  file: PortalReviewFile;
}) {
  const { url, loading, error } = useReviewFileUrl(review, file);

  if (loading) {
    return (
      <div className="h-36 rounded-xl bg-[#F8F8F6] border border-[#E8E8E4] flex items-center justify-center text-xs text-[#8A8A85]">
        Loading {file.kind}…
      </div>
    );
  }
  if (error || !url) {
    return (
      <div className="h-24 rounded-xl bg-[#F8F8F6] border border-[#E8E8E4] flex items-center justify-center text-xs text-red-500 px-2 text-center">
        {error || "File unavailable"}
      </div>
    );
  }
  if (file.kind === "video") {
    return (
      <video
        src={url}
        controls
        className="w-full max-h-64 rounded-xl bg-black"
      />
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt={file.fileName}
        className="w-full max-h-64 object-contain rounded-xl bg-[#F8F8F6] border border-[#E8E8E4]"
      />
    </a>
  );
}

export default function ReviewCard({
  review,
  canManage,
  showEmployee = true,
}: {
  review: PortalReview;
  canManage?: boolean;
  showEmployee?: boolean;
}) {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const updatingId = useAppSelector((state) => state.portalReviews.updatingId);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(review.admin_note || "");
  const screenshots = review.files?.filter((file) => file.kind === "screenshot") || [];
  const videos = review.files?.filter((file) => file.kind === "video") || [];
  const busy = updatingId === review.review_id;

  const setStatus = async (status: PortalReviewStatus) => {
    try {
      await dispatch(
        updatePortalReviewStatus({
          reviewId: review.review_id,
          status,
          admin_note: note,
        }),
      ).unwrap();
      showToast("Feedback updated", "success");
    } catch (err) {
      showToast(typeof err === "string" ? err : "Could not update feedback", "error");
    }
  };

  return (
    <article className="rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-[#4A4A48] truncate">{review.title}</h3>
            <span
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${STATUS_CLASS[review.status]}`}
            >
              {PORTAL_REVIEW_STATUS_LABEL[review.status]}
            </span>
            {review.folder_key ? (
              <span className="text-[11px] text-[#8A8A85]">{review.folder_key}</span>
            ) : null}
          </div>
          {showEmployee ? (
            <p className="text-xs text-[#8A8A85] mt-1">
              {review.emp_name} · {review.emp_role} · {formatAdminDateTime(review.created_at)}
            </p>
          ) : (
            <p className="text-xs text-[#8A8A85] mt-1">
              {formatAdminDateTime(review.created_at)}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          className="text-xs font-medium text-[#2fa9bf] hover:underline"
        >
          {open ? "Hide details" : "View details"}
        </button>
      </div>

      <p className="text-sm text-[#4A4A48] mt-3 whitespace-pre-wrap">
        {open ? review.message : review.message.slice(0, 180)}
        {!open && review.message.length > 180 ? "…" : ""}
      </p>

      {(screenshots.length || videos.length) && !open ? (
        <div className="flex gap-3 mt-3 text-xs text-[#8A8A85]">
          {screenshots.length ? (
            <span className="inline-flex items-center gap-1">
              <FaImage /> {screenshots.length} screenshot{screenshots.length === 1 ? "" : "s"}
            </span>
          ) : null}
          {videos.length ? (
            <span className="inline-flex items-center gap-1">
              <FaVideo /> {videos.length} video{videos.length === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
      ) : null}

      {open ? (
        <div className="mt-4 space-y-3">
          {screenshots.length ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {screenshots.map((file) => (
                <ReviewMedia key={file.boxFileId} review={review} file={file} />
              ))}
            </div>
          ) : null}
          {videos.length ? (
            <div className="space-y-3">
              {videos.map((file) => (
                <ReviewMedia key={file.boxFileId} review={review} file={file} />
              ))}
            </div>
          ) : null}

          {review.admin_note && !canManage ? (
            <p className="text-xs text-[#8A8A85] bg-[#F8F8F6] rounded-xl px-3 py-2">
              Admin note: {review.admin_note}
            </p>
          ) : null}

          {canManage ? (
            <div className="rounded-xl bg-[#F8F8F6] p-3 space-y-2">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note for the developer / employee (optional)"
                className="w-full min-h-[70px] rounded-xl border border-[#DDDDD8] bg-white px-3 py-2 text-sm outline-none focus:border-[#2fa9bf]"
              />
              <div className="flex flex-wrap gap-2">
                {(["useful", "not_useful", "implemented", "pending"] as PortalReviewStatus[]).map(
                  (status) => (
                    <button
                      key={status}
                      type="button"
                      disabled={busy}
                      onClick={() => setStatus(status)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                        review.status === status
                          ? "bg-[#2fa9bf] text-white border-[#2fa9bf]"
                          : "bg-white text-[#4A4A48] border-[#DDDDD8] hover:bg-white/80"
                      } disabled:opacity-50`}
                    >
                      {PORTAL_REVIEW_STATUS_LABEL[status]}
                    </button>
                  ),
                )}
              </div>
              {review.reviewed_by ? (
                <p className="text-[11px] text-[#8A8A85]">
                  Last updated by {review.reviewed_by}
                  {review.reviewed_at ? ` · ${formatAdminDateTime(review.reviewed_at)}` : ""}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
